import { useCallback, useMemo } from "react";

import { useProcesses } from "@/api/queries/processes";
import { humanize } from "@/lib/labels";

/**
 * Display-name resolver for backend ``process_type`` slugs.
 *
 * Single source is the fetched process row (``GET /processes`` — the
 * ``display_name`` is already resolved per UI language by the backend);
 * unknown slugs fall back to ``humanize()`` so the UI never leaks
 * ``surface_finishes`` raw. Replaces the deleted ``admin:processType.*``
 * i18n catalog — process names are platform data, not FE locale keys.
 *
 * 🪤 **Dokud dotaz běží, se slug nehádá.** ``humanize()`` je fallback pro
 * slug, který v odpovědi NENÍ — ne pro slug, jehož jméno se teprve veze.
 * Než se to rozlišilo, ukázal detail položky „DOSTUPNÉ PROCESY: Print 3d"
 * a o vteřinu později tutéž hodnotu jako „3D tisk" (KAN-703). Po dobu
 * načítání se proto vrací tichá výpustka, kterou admin používá všude
 * jinde pro „ještě nevím".
 */
export function useProcessTypeLabel(): (
  value: string | null | undefined,
  fallback?: string,
) => string {
  const processes = useProcesses({ includeNonEntry: true });
  // Chyba dotazu je taky odpověď: „…" navždy by bylo horší než dohad.
  const settled = !processes.isPending;
  const names = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of processes.data ?? []) {
      if (p.display_name) map.set(p.name, p.display_name);
    }
    return map;
  }, [processes.data]);
  return useCallback(
    (value: string | null | undefined, fallback?: string) => {
      if (!value) return fallback ?? "—";
      const known = names.get(value);
      if (known) return known;
      return settled ? humanize(value) : fallback ?? "…";
    },
    [names, settled],
  );
}

/**
 * KAN-2114 — jen procesy, které obchod nabízí. Platformní katalogová položka
 * nese ``process_types`` všech procesů, pro které se materiál hodí
 * (laser, plazma, vodní paprsek); obchod jen s laserem má vidět laser.
 * Zdroj je týž ``GET /processes`` (filtrovaný podle schopností obchodu).
 * Dokud se seznam veze, nic se neskrývá.
 */
export function useShopProcessFilter(): (processTypes: readonly string[]) => string[] {
  const processes = useProcesses({ includeNonEntry: true });
  const available = useMemo(
    () =>
      Array.isArray(processes.data)
        ? new Set(processes.data.filter((p) => p.is_available).map((p) => p.name))
        : null,
    [processes.data],
  );
  return useCallback(
    (processTypes: readonly string[]) =>
      available ? processTypes.filter((p) => available.has(p)) : [...processTypes],
    [available],
  );
}
